import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import AnimationCard from '../components/AnimationCard';
import AnimationCarousel from '../components/AnimationCarousel';
import ConnectedAppsSection from '../components/ConnectedAppsSection';
import Pagination from '../components/Pagination';
import TagSearchInput from '../components/TagSearchInput';
import Spinner from '../components/Spinner';
import {
  fetchAnimations,
  fetchAnimationTagCounts,
  fetchCategories,
  fetchFollowingAnimations,
  fetchMyAnimations,
  fetchTags,
} from '../api/client';
import type { AnimationTagCount, CategoryInfo, ListPageOptions, PagedAnimations, TagInfo } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { parseSearchQuery, matchAnimation } from '../lib/searchQuery';
import { sortByRecommended } from '../lib/recommend';
import type { Animation } from '../types';

type View = 'top' | 'list';
type SortKey = 'new' | 'popular' | 'recommended';
type ScopeKey = 'all' | 'following' | 'mine';

const PAGE_SIZE = 24;
// URL の ?category= に使う「未分類」専用の予約値。カテゴリ名との衝突は運用上避けてもらう想定
// （タグの「公式サンプル」予約名と同様の扱い）。
const UNCATEGORIZED_KEY = '__uncategorized__';
/** トップページの各カルーセルに出す件数。 */
const SECTION_LIMIT = 12;

/** URL の sort パラメータを検証する。未指定・未知の値は 'new' 扱い。 */
function parseSort(value: string | null): SortKey {
  if (value === 'popular' || value === 'recommended') return value;
  return 'new';
}

/** URL の scope パラメータを検証する。未指定・未知の値は 'all' 扱い。 */
function parseScope(value: string | null): ScopeKey {
  if (value === 'following' || value === 'mine') return value;
  return 'all';
}

/** URL の page パラメータを検証する。正の整数以外は 1 ページ目扱い。 */
function parsePage(value: string | null): number {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : 1;
}

/**
 * 後方互換: 旧 ?tab= / ?tag= と、他ページからの ?q= 単独リンクを ?view=list に正規化する。
 * 旧 ?tag= は新しい ?tags= に移行する。
 * source は書き換えず、コピーに対して変換した結果と「変更があったか」を返す純関数。
 */
function normalizeSearchParams(source: URLSearchParams): { params: URLSearchParams; changed: boolean } {
  const params = new URLSearchParams(source);
  let changed = false;
  const legacyTab = params.get('tab');
  if (legacyTab !== null) {
    params.delete('tab');
    changed = true;
    if (legacyTab === 'popular') {
      params.set('view', 'list');
      params.set('sort', 'popular');
    } else if (legacyTab === 'following') {
      params.set('view', 'list');
      params.set('scope', 'following');
    } else if (legacyTab === 'mine') {
      params.set('view', 'list');
      params.set('scope', 'mine');
    }
    // legacyTab === 'new' はトップへ（view を付けずそのまま）。
  }
  const legacyTag = params.get('tag');
  if (legacyTag !== null) {
    params.delete('tag');
    params.set('view', 'list');
    changed = true;
    const merged = [...new Set([...(params.get('tags') ?? '').split(',').map((tag) => tag.trim()).filter(Boolean), ...legacyTag.split(',').map((tag) => tag.trim()).filter(Boolean)])];
    if (merged.length) params.set('tags', merged.join(','));
  }
  if (!params.has('view') && params.has('q')) {
    params.set('view', 'list');
    changed = true;
  }
  return { params, changed };
}

export default function Animations() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // 正規化後のパラメータをレンダリング時に直接使う。effect で URL を書き換えてから
  // 再レンダリングを待つ方式（旧 useLayoutEffect 版）だと、HashRouter（親）が history を
  // 購読するのも useLayoutEffect のため、初回マウント時は子である本コンポーネントの
  // useLayoutEffect が先に走って「購読開始前」に URL を書き換えてしまい、ルーターの
  // state が更新されず画面が古いままになる不具合があった。正規化はレンダー中に完結させ、
  // URL への反映（表示上の追従）だけを下の useEffect で行う。
  const { params: normalizedParams, changed: needsUrlSync } = useMemo(
    () => normalizeSearchParams(searchParams),
    [searchParams],
  );

  useEffect(() => {
    if (needsUrlSync) setSearchParams(normalizedParams, { replace: true });
  }, [needsUrlSync, normalizedParams, setSearchParams]);

  const view: View = normalizedParams.get('view') === 'list' ? 'list' : 'top';
  const sort = parseSort(normalizedParams.get('sort'));
  const scope = parseScope(normalizedParams.get('scope'));
  const query = normalizedParams.get('q') ?? '';
  const parsedQuery = useMemo(() => parseSearchQuery(query), [query]);
  const selectedTags = useMemo(
    () => [...new Set([...(normalizedParams.get('tags') ?? '').split(',').map((tag) => tag.trim()).filter(Boolean), ...parsedQuery.tags])],
    [normalizedParams, parsedQuery],
  );
  const selectedTagsKey = selectedTags.join(',');
  // カテゴリは単一選択。サイドバーの絞り込み専用（#タグ検索とは独立）。
  const selectedCategory = normalizedParams.get('category') ?? '';

  const [masterTags, setMasterTags] = useState<TagInfo[]>([]);
  useEffect(() => {
    fetchTags()
      .then(setMasterTags)
      .catch(() => setMasterTags([]));
  }, []);
  const allTagNames = useMemo(() => masterTags.map((t) => t.name), [masterTags]);

  const [masterCategories, setMasterCategories] = useState<CategoryInfo[]>([]);
  useEffect(() => {
    fetchCategories()
      .then(setMasterCategories)
      .catch(() => setMasterCategories([]));
  }, []);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ---- トップビュー用データ ---------------------------------------------------
  const [spotlightPool, setSpotlightPool] = useState<Animation[]>([]);
  const [followingPreview, setFollowingPreview] = useState<Animation[]>([]);
  const [minePreview, setMinePreview] = useState<Animation[]>([]);

  useEffect(() => {
    if (view !== 'top') return;
    let cancelled = false;
    setLoading(true);
    setError('');
    // following / mine は未ログインだと 401 になるため、ログイン時のみ取得する。
    // 一部のエンドポイントが失敗しても他セクションは表示したいため allSettled で個別に扱う
    // （MyPage.tsx の書き方に倣う）。
    const emptyPage: PagedAnimations = { items: [] };
    Promise.allSettled([
      fetchAnimations(),
      user ? fetchFollowingAnimations() : Promise.resolve(emptyPage),
      user ? fetchMyAnimations() : Promise.resolve(emptyPage),
    ])
      .then(([allRes, followRes, mineRes]) => {
        if (cancelled) return;
        setSpotlightPool(allRes.status === 'fulfilled' ? allRes.value.items : []);
        setFollowingPreview(followRes.status === 'fulfilled' ? followRes.value.items : []);
        setMinePreview(mineRes.status === 'fulfilled' ? mineRes.value.items : []);
        // 注目/新着/人気 の元になる一覧が取れないと主要セクションが崩れるため、その失敗のみエラー表示する。
        if (allRes.status === 'rejected') setError(String(allRes.reason));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [view, user]);

  const spotlightItems = useMemo(
    () => sortByRecommended(spotlightPool).slice(0, SECTION_LIMIT),
    [spotlightPool],
  );
  const newItems = useMemo(
    () =>
      [...spotlightPool]
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, SECTION_LIMIT),
    [spotlightPool],
  );
  const popularItems = useMemo(
    () => [...spotlightPool].sort((a, b) => b.likes - a.likes).slice(0, SECTION_LIMIT),
    [spotlightPool],
  );
  const followingItems = useMemo(() => followingPreview.slice(0, SECTION_LIMIT), [followingPreview]);
  const mineItems = useMemo(() => minePreview.slice(0, SECTION_LIMIT), [minePreview]);
  // 「人気のタグ」は直近の公開アニメーション（新着100件 = spotlightPool）内での使用数から算出する。
  // タグマスタの登録新着順（masterTags）を出すと人気度と無関係になってしまうため。
  const heroTags = useMemo(() => {
    const stats = new Map<string, { count: number; likes: number }>();
    for (const a of spotlightPool) {
      for (const name of new Set(a.tags)) {
        const s = stats.get(name) ?? { count: 0, likes: 0 };
        s.count += 1;
        s.likes += a.likes;
        stats.set(name, s);
      }
    }
    return [...stats.entries()]
      .sort(([nameA, a], [nameB, b]) => {
        if (a.count !== b.count) return b.count - a.count;
        if (a.likes !== b.likes) return b.likes - a.likes;
        return nameA.localeCompare(nameB);
      })
      .slice(0, 6)
      .map(([name]) => name);
  }, [spotlightPool]);

  // ---- 一覧ビュー用データ -------------------------------------------------------
  const [listItems, setListItems] = useState<Animation[]>([]);
  const [listTagCounts, setListTagCounts] = useState<AnimationTagCount[]>([]);
  // カーソルページングの「続き」の位置。無ければ最終ページまで読み切っている。
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);

  // scope（表示範囲）ごとの一覧取得関数。
  const fetcher =
    scope === 'following' ? fetchFollowingAnimations : scope === 'mine' ? fetchMyAnimations : fetchAnimations;

  const listOptions: ListPageOptions = {
    tags: selectedTags,
    q: parsedQuery.text,
    ...(selectedCategory ? { category: selectedCategory } : {}),
  };

  useEffect(() => {
    if (view !== 'list' || ((scope === 'following' || scope === 'mine') && !user)) {
      setListTagCounts([]);
      return;
    }
    let cancelled = false;
    fetchAnimationTagCounts(scope === 'all' ? 'public' : scope, listOptions)
      .then((counts) => { if (!cancelled) setListTagCounts(counts); })
      .catch(() => { if (!cancelled) setListTagCounts([]); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, scope, user, selectedTagsKey, parsedQuery.text, selectedCategory]);

  useEffect(() => {
    if (view !== 'list') return;
    // フォロー中 / マイアセットは未ログインだと 401 になるため、空状態の表示に任せて取得自体をしない。
    if ((scope === 'following' || scope === 'mine') && !user) {
      setListItems([]);
      setNextCursor(undefined);
      setError('');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    // scope が変わった＝表示対象が変わったので、リストとカーソルを先頭からやり直す。
    setNextCursor(undefined);
    let cancelled = false;
    fetcher(listOptions)
      .then(({ items, nextCursor: next }) => {
        if (cancelled) return;
        setListItems(items);
        setNextCursor(next);
      })
      .catch((e) => { if (!cancelled) setError(String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, scope, user, selectedTagsKey, parsedQuery.text, selectedCategory]);

  const handleLoadMore = () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    fetcher({ ...listOptions, cursor: nextCursor })
      .then(({ items, nextCursor: next }) => {
        setListItems((prev) => [...prev, ...items]);
        setNextCursor(next);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoadingMore(false));
  };

  const categoryCounts = useMemo(() => {
    // サイドバーのカテゴリ件数は「現在の scope のデータ内」での件数（検索・カテゴリ絞り込みの影響は受けない）。
    const counts = new Map<string, number>();
    let uncategorized = 0;
    for (const a of listItems) {
      if (a.category) counts.set(a.category, (counts.get(a.category) ?? 0) + 1);
      else uncategorized++;
    }
    return { counts, uncategorized };
  }, [listItems]);

  const filteredItems = useMemo(() => {
    return listItems.filter((a) => {
      if (!matchAnimation(a, { text: parsedQuery.text, tags: selectedTags })) return false;
      if (selectedCategory === UNCATEGORIZED_KEY) return !a.category;
      if (selectedCategory) return a.category === selectedCategory;
      return true;
    });
  }, [listItems, parsedQuery, selectedTagsKey, selectedCategory]);

  const sortedItems = useMemo(() => {
    if (sort === 'popular') return [...filteredItems].sort((a, b) => b.likes - a.likes);
    if (sort === 'recommended') return sortByRecommended(filteredItems);
    return [...filteredItems].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [filteredItems, sort]);

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / PAGE_SIZE));
  // URL の page が総ページ数を超えている場合は最後のページにクランプする（URL 自体は書き換えない）。
  const currentPage = Math.min(parsePage(normalizedParams.get('page')), totalPages);
  const pageItems = sortedItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // 検索欄の入力中はここに溜め、検索ボタン押下 / Enter で初めて q（URL）へ反映する。
  const [heroDraft, setHeroDraft] = useState('');
  const [listDraft, setListDraft] = useState(query);

  // ブラウザの戻る/進む、他ページからの q 付き遷移など URL 側の変化を入力欄に同期する。
  useEffect(() => {
    setListDraft(query);
  }, [query]);

  const handleHeroSearchSubmit = (e: FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    params.set('view', 'list');
    if (heroDraft) params.set('q', heroDraft);
    navigate(`/?${params.toString()}`);
  };

  const goToTag = (name: string) => {
    const params = new URLSearchParams();
    params.set('view', 'list');
    params.set('tags', name);
    navigate(`/?${params.toString()}`);
  };

  const handleListSearchSubmit = (e: FormEvent) => {
    e.preventDefault();
    const parsedDraft = parseSearchQuery(listDraft);
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      const tags = [...new Set([...(params.get('tags') ?? '').split(',').map((tag) => tag.trim()).filter(Boolean), ...parsedDraft.tags])];
      if (parsedDraft.text) {
        params.set('q', parsedDraft.text);
      } else {
        params.delete('q');
      }
      if (tags.length) params.set('tags', tags.join(','));
      params.delete('page');
      return params;
    });
    setListDraft(parsedDraft.text);
  };

  const toggleTag = (name: string) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      const tags = new Set(selectedTags);
      if (tags.has(name)) tags.delete(name);
      else tags.add(name);
      if (tags.size) params.set('tags', [...tags].join(','));
      else params.delete('tags');
      if (parsedQuery.text) params.set('q', parsedQuery.text);
      else params.delete('q');
      params.delete('page');
      return params;
    });
    setListDraft(parsedQuery.text);
  };

  const setSort = (next: SortKey) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === 'new') {
        params.delete('sort');
      } else {
        params.set('sort', next);
      }
      params.delete('page');
      return params;
    });
  };

  const setScope = (next: ScopeKey) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === 'all') {
        params.delete('scope');
      } else {
        params.set('scope', next);
      }
      params.delete('page');
      return params;
    });
  };

  // カテゴリは単一選択。選択中のカテゴリをもう一度クリックすると解除する。
  const toggleCategory = (key: string) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      const current = params.get('category') ?? '';
      if (key === '' || current === key) {
        params.delete('category');
      } else {
        params.set('category', key);
      }
      params.delete('page');
      return params;
    });
  };

  const clearFilters = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete('tag');
      params.delete('tags');
      params.delete('category');
      params.delete('q');
      params.delete('scope');
      params.delete('page');
      return params;
    });
  };

  const setPage = (next: number) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === 1) {
        params.delete('page');
      } else {
        params.set('page', String(next));
      }
      return params;
    });
    window.scrollTo({ top: 0 });
  };

  const showLoginNotice = (scope === 'following' || scope === 'mine') && !user;

  if (view === 'list') {
    return (
      <div className="container container--wide">
        <nav className="crumbs" aria-label="パンくずリスト">
          <Link to="/">ホーム</Link>
          <span aria-hidden="true">›</span>
          <span className="crumbs__current">アニメーション一覧</span>
        </nav>

        <form className="search-form" onSubmit={handleListSearchSubmit}>
          <TagSearchInput
            className="hero__search"
            value={listDraft}
            onChange={setListDraft}
            availableTags={allTagNames}
            placeholder="タイトル・作者・#タグ で検索"
          />
          <button type="submit" className="btn btn--primary">
            検索
          </button>
        </form>

        {(listTagCounts.length > 0 || selectedTags.length > 0) && (
          <div className="animation-tag-filter" aria-label="タグで絞り込む">
            <span className="animation-tag-filter__label">よく使われるタグ</span>
            <div className="animation-tag-filter__chips">
              {[...new Map([
                ...selectedTags.map((name) => [name, { name, count: 0 }] as const),
                ...listTagCounts.map((item) => [item.name, item] as const),
              ]).values()].map(({ name, count }) => (
                <button
                  key={name}
                  type="button"
                  className={`animation-tag-filter__chip${selectedTags.includes(name) ? ' is-selected' : ''}`}
                  aria-pressed={selectedTags.includes(name)}
                  onClick={() => toggleTag(name)}
                >
                  #{name}<span>{count}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="list-layout">
          <aside className="list-layout__sidebar">
            <div className="filter-group">
              <h3>表示範囲</h3>
              <div className="radio-group">
                <label className="radio">
                  <input
                    type="radio"
                    name="scope"
                    checked={scope === 'all'}
                    onChange={() => setScope('all')}
                  />
                  すべて
                </label>
                {/* フォロー中 / マイアセットは未ログインだと意味を持たないため、ログイン中のみ出す。 */}
                {user && (
                  <label className="radio">
                    <input
                      type="radio"
                      name="scope"
                      checked={scope === 'following'}
                      onChange={() => setScope('following')}
                    />
                    フォロー中
                  </label>
                )}
                {user && (
                  <label className="radio">
                    <input
                      type="radio"
                      name="scope"
                      checked={scope === 'mine'}
                      onChange={() => setScope('mine')}
                    />
                    マイアセット
                  </label>
                )}
              </div>
            </div>

            <div className="filter-group">
              <h3>カテゴリ</h3>
              <div className="filter-tag-list">
                <label className="filter-tag-row">
                  <span className="filter-tag-row__check">
                    <input
                      type="radio"
                      name="category"
                      checked={selectedCategory === ''}
                      onClick={() => toggleCategory('')}
                      onChange={() => {}}
                    />
                    すべて
                  </span>
                </label>
                {masterCategories.map((c) => (
                  <label className="filter-tag-row" key={c.name}>
                    <span className="filter-tag-row__check">
                      <input
                        type="radio"
                        name="category"
                        checked={selectedCategory === c.name}
                        onClick={() => toggleCategory(c.name)}
                        onChange={() => {}}
                      />
                      {c.name}
                    </span>
                    <span className="filter-tag-row__count">{categoryCounts.counts.get(c.name) ?? 0}</span>
                  </label>
                ))}
                <label className="filter-tag-row">
                  <span className="filter-tag-row__check">
                    <input
                      type="radio"
                      name="category"
                      checked={selectedCategory === UNCATEGORIZED_KEY}
                      onClick={() => toggleCategory(UNCATEGORIZED_KEY)}
                      onChange={() => {}}
                    />
                    未分類
                  </span>
                  <span className="filter-tag-row__count">{categoryCounts.uncategorized}</span>
                </label>
              </div>
            </div>

            <button type="button" className="btn" onClick={clearFilters}>
              条件をクリア
            </button>
          </aside>

          <div className="list-layout__main">
            <div className="section-title">
              <h2>
                検索結果 <span className="section-title__count">{sortedItems.length}件</span>
              </h2>
              <div className="sort-seg">
                <button
                  type="button"
                  className={`sort-seg__btn${sort === 'new' ? ' on' : ''}`}
                  onClick={() => setSort('new')}
                >
                  新着順
                </button>
                <button
                  type="button"
                  className={`sort-seg__btn${sort === 'popular' ? ' on' : ''}`}
                  onClick={() => setSort('popular')}
                >
                  人気順
                </button>
                <button
                  type="button"
                  className={`sort-seg__btn${sort === 'recommended' ? ' on' : ''}`}
                  onClick={() => setSort('recommended')}
                >
                  おすすめ順
                </button>
              </div>
            </div>

            {showLoginNotice ? (
              <p className="follow-empty">
                {scope === 'following'
                  ? 'フォロー中のユーザーはログインすると表示できます。'
                  : 'マイアセットはログインすると表示できます。'}
              </p>
            ) : loading ? (
              // 切り替え直後は古い一覧を残さず Spinner のみ出す（読み込み中に前の結果がちらつくのを防ぐ）。
              <Spinner />
            ) : error ? (
              <p className="viewer__error">{error}</p>
            ) : (
              <>
                {sortedItems.length === 0 && <p>該当するアニメーションがありません。</p>}

                <div className="grid">
                  {pageItems.map((a) => (
                    <AnimationCard key={a.id} animation={a} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setPage} />
                )}

                {nextCursor && (
                  <div className="load-more">
                    <button type="button" className="btn" onClick={handleLoadMore} disabled={loadingMore}>
                      {loadingMore ? '読み込み中…' : 'もっと見る'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <section className="explore-bar">
        <div className="explore-bar__inner">
          <form className="search-form" onSubmit={handleHeroSearchSubmit}>
            <TagSearchInput
              className="hero__search"
              value={heroDraft}
              onChange={setHeroDraft}
              availableTags={allTagNames}
              placeholder="タイトル・作者・#タグ で検索"
            />
            <button type="submit" className="btn btn--primary">
              検索
            </button>
          </form>
          {heroTags.length > 0 && (
            <div className="explore-bar__tags">
              <span className="explore-bar__tags-label">人気のタグ</span>
              {heroTags.map((t) => (
                <button
                  type="button"
                  key={t}
                  className="explore-bar__tag"
                  onClick={() => goToTag(t)}
                >
                  #{t}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <div className="container container--wide">
        <ConnectedAppsSection />
        {loading ? (
          <Spinner />
        ) : error ? (
          <p className="viewer__error">{error}</p>
        ) : (
          <>
            <AnimationCarousel
              title="注目のアニメーション"
              items={spotlightItems}
              moreHref="/?view=list&sort=recommended"
              size="lg"
            />
            <AnimationCarousel title="新着" items={newItems} moreHref="/?view=list&sort=new" />
            <AnimationCarousel title="人気" items={popularItems} moreHref="/?view=list&sort=popular" />
            {user && (
              <AnimationCarousel
                title="フォロー中"
                items={followingItems}
                moreHref="/?view=list&scope=following"
              />
            )}
            {user && (
              <AnimationCarousel
                title="マイアセット"
                items={mineItems}
                moreHref="/?view=list&scope=mine"
              />
            )}
          </>
        )}
      </div>
    </>
  );
}
